/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { styled } from '@mui/material/styles';
import CircleIcon from '@mui/icons-material/Circle';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem, { ListItemProps } from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import theme from '../../theme';
import { Action, ProcessStatus } from '../../models/batch';

export enum Activity {
  'bulkDelete' = 'Bulk Delete',
  'bulkUpdate' = 'Bulk Update',
  'import' = 'Import',
  'export' = 'Export'
}

type Status = ProcessStatus;
export interface Props extends ListItemProps {
  activity: Activity | Action;
  primaryText: string;
  caption: string;
  isLastItem:boolean;
  secondaryText?: string;
  url?: string;
  status?: Status;
  statusValue?: number;
}

const StyledListItem = styled(ListItem)({
  padding: theme.spacing(3),
  '&:hover': {
    background: theme.palette.action.hover,
  },
});

const IconColorMap = {
  pending: theme.palette.caution.main,
  succeeded: theme.palette.success.main,
  failed: theme.palette.error.main,
  cancelled: theme.palette.gray.main,
};

export default function MenuFeedItem({ ...props }: Props) {
  const preposition = () => {
    switch (props.activity) {
      case Activity.bulkDelete:
        return 'from ';
      case Activity.import:
        return 'to ';
      default:
        return '';
    }
  };

  function renderCaptionIcon() {
    if (props.status === 'pending') {
      return (<CircularProgress color="caution" size={20} />);
    }

    if (props.status === 'running') {
      return <CircularProgress color="secondary" variant="determinate" size={20} value={props.statusValue} />;
    }

    return (
      <CircleIcon sx={{ color: IconColorMap[props.status!], fontSize: 10 }} />
    );
  }

  return (
    <Box>
      <StyledListItem
        disablePadding
        {...(props.divider && {
          divider: true,
        })}
      >
        <Stack width="100%">
          <Stack height={24}>
            <ListItemText
              sx={{ m: 0 }}
              primary={(
                <Typography variant="overline" color="gray.main">
                  {(props.activity as Action)?.description?.toUpperCase()}
                </Typography>
              )}
              primaryTypographyProps={{ noWrap: true }}
            />
          </Stack>
          <ListItemText
            sx={{ m: 0 }}
            primary={(
              <Typography variant="h5" color="gray.dark">
                {props.primaryText}
              </Typography>
            )}
            primaryTypographyProps={{ noWrap: true }}
            secondary={(
              <>
                {props.url ? (
                  <>
                    <Typography sx={{ display: 'inline' }} variant="body2">
                      {preposition()}
                    </Typography>
                    {/** NOTE  iterate on this to allow for other routes and actions */}
                    <Link
                      href={props.url || '#'}
                      target="_blank"
                      rel="noopener"
                    >
                      {props.secondaryText}
                    </Link>
                  </>
                ) : (
                  <Typography sx={{ display: 'inline' }} variant="body2">
                    {props.secondaryText}
                  </Typography>
                )}
              </>
            )}
            secondaryTypographyProps={{ noWrap: true, component: 'span' }}
          />
          {props.caption && (
            <Stack direction="row" alignItems="center">
              {props.status && renderCaptionIcon()}
              <ListItemText
                sx={{ m: 0 }}
                primary={(
                  <Typography
                    variant="caption"
                    color={props.status && (props.status === 'failed')
                      ? theme.palette.error.main : 'gray.dark'}
                    {...(props.status && { ml: 1 })}
                    noWrap
                  >
                    {props.caption}
                  </Typography>
                )}
                primaryTypographyProps={{ noWrap: true }}
              />
            </Stack>
          )}
        </Stack>
      </StyledListItem>
      {!props.isLastItem && (<Divider />)}
    </Box>
  );
}
